import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"

import {
  Container,
  FilterContainer,
  SelectorContainer,
  InputContainer,
  SliderContainer,
} from "./index.style"

import { colorIsDark } from "Utils/colors.js"

import { useClickOutside } from "Hooks/useClickOutside"

const RangeFilter = ({
  // Props
  placeholder,
  rangeValue,
  value,
  affectedField,
  dataField,
  data,
  rawData,
  // Functions
  onSelectFilterValue,
  onRemoveSelectedValue,
  // Style
  activeBorder,
  activeRadius,
  defaultColor,
  dropdownBorderColor,
  dropdownBorderRadius,
  dropdownBorderSize,
  dropdownBorderStyle,
  sliderColor,
}) => {
  /* State */
  const [isSelectorOpen, setIsSelectorOpen] = useState(false)
  const [isRangeSelected, setIsRangeSelected] = useState(false)
  const [backgroundSliderProperty, setBackgroundSliderProperty] = useState("")
  const [maxRange, setMaxRange] = useState("100")
  const [maxValue, setMaxValue] = useState("100")
  const [minRange, setMinRange] = useState("0")
  const [minValue, setMinValue] = useState("0")
  const [timeoutRequest, setTimeoutRequest] = useState()

  const selectorRef = useRef()
  const inputStep = ((maxRange - minRange) / 100).toFixed(0)

  /* Lifecycle */
  useEffect(() => {
    let values = data?.unique ? [...data.unique] : []

    const [min, max] = getMinMaxValue(values)
    setMinRange(min)
    setMaxRange(max)

    // if filter have a seleted range and no groupBy
    if (value && Array.isArray(value) && value.length === 2 && affectedField.length === 0) {
      if (value[0] < min) {
        setMinValue(min)
      } else {
        setMinValue(value[0])
      }
      if (value[1] > max) {
        setMaxValue(max)
      } else {
        setMaxValue(value[1])
      }
      setIsRangeSelected(true)
    } else if (
      rangeValue &&
      Array.isArray(rangeValue) &&
      rangeValue.length === 2 &&
      affectedField.length === 1
    ) {
      if (rangeValue[0] < min) {
        setMinValue(min)
      } else {
        setMinValue(rangeValue[0])
      }
      if (rangeValue[1] > max) {
        setMaxValue(max)
      } else {
        setMaxValue(rangeValue[1])
      }
      setIsRangeSelected(true)
    } else {
      setMinValue(min)
      setMaxValue(max)
    }
  }, [data])

  useEffect(() => {
    if (isSelectorOpen) {
      var fixedLayout = document.getElementById("resizable")
      if (fixedLayout) {
        fixedLayout.style.removeProperty("overflow")
        fixedLayout.style.removeProperty("overflow-y")
      }

      var screenshotContainer = document.getElementById("screenshotContainer")
      if (screenshotContainer) screenshotContainer.style.setProperty("overflow-y", "auto")
    } else {
      var fixedLayout = document.getElementById("resizable")
      if (fixedLayout) fixedLayout.style.setProperty("overflow-y", "auto")
      var screenshotContainer = document.getElementById("screenshotContainer")
      if (screenshotContainer) screenshotContainer.style.removeProperty("overflow-y")
    }
  }, [isSelectorOpen])

  useEffect(() => {
    fillRangeColor()
  }, [sliderColor, maxValue, minValue])

  useEffect(() => {
    if (maxValue !== maxRange || minValue !== minRange) {
      onSendFilter()
    }
  }, [maxValue, minValue])

  useClickOutside(selectorRef, () => {
    if (isSelectorOpen) {
      setIsSelectorOpen(false)
    }
  })

  /* Functions */
  const fillRangeColor = () => {
    let deltaRange = maxRange - minRange
    if (deltaRange === 0) {
      deltaRange = 0.00000000001
    }
    const percent1 = ((minValue - minRange) / deltaRange) * 100
    const percent2 = ((maxValue - minRange) / deltaRange) * 100
    setBackgroundSliderProperty(
      `linear-gradient(to right, #dadae5 ${percent1}% , ${sliderColor} ${percent1}% , ${sliderColor} ${percent2}%, #dadae5 ${percent2}%)`
    )
  }

  const getMinMaxValue = arr => {
    if (!Array.isArray(arr) || arr.length === 0) {
      return [-1, -1]
    }
    return [Math.min(...arr), Math.max(...arr)]
  }

  const onChangeValue = e => {
    const newValue = e?.target?.value
    const name = e?.target?.name

    if (name === "minValueInput") {
      setMinValue(newValue)
    } else if (name === "maxValueInput") {
      setMaxValue(newValue)
    }

    setIsRangeSelected(true)
  }

  const onResetSelection = () => {
    onRemoveSelectedValue()
    setIsRangeSelected(false)
    setMinValue(minRange)
    setMaxValue(maxRange)
  }

  const onSendFilter = () => {
    let min = minValue
    let max = maxValue

    if (typeof min === "string") {
      min = parseInt(min)
    }
    if (typeof max === "string") {
      max = parseInt(max)
    }

    let filtersToReturn

    // if filter have groupBy, filter by groupBy field which respect the value range
    if (affectedField && affectedField.length) {
      // find all dataFields that are in the range
      const dataRangeFiltered = rawData.filter(
        dataItem => dataItem[dataField] >= min && dataItem[dataField] <= max
      )

      filtersToReturn = {
        value: dataRangeFiltered.map(value => value[affectedField]),
        operation: "==",
        rangeSelected: [min, max],
      }
    } else {
      filtersToReturn = {
        value: [min, max],
        operation: [">=", "<="],
      }
    }
    if (timeoutRequest) {
      clearTimeout(timeoutRequest)
    }

    setTimeoutRequest(
      setTimeout(function () {
        onSelectFilterValue(filtersToReturn)
      }, 2000)
    )
  }

  const onSlide = e => {
    const newValue = e?.target?.value
    const name = e?.target?.name

    if (name === "minValueSlider" && newValue < maxValue) {
      setMinValue(newValue)
    } else if (name === "maxValueSlider" && newValue > minValue) {
      setMaxValue(newValue)
    }

    setIsRangeSelected(true)
  }

  const onSwapValuesIfNecessary = () => {
    let min = minValue
    let max = maxValue

    if (!min) {
      min = minRange
    }
    if (!max) {
      max = maxRange
    }

    if (min > max) {
      min = maxValue
      max = minValue
    } else if (min < minRange) {
      min = minRange
    } else if (max > maxRange) {
      max = maxRange
    }

    setMinValue(min)
    setMaxValue(max)
  }

  /* Render */
  return (
    <Container>
      <FilterContainer
        activeBorder={activeBorder}
        activeRadius={activeRadius}
        backgroundIsDark={colorIsDark(defaultColor)}
        borderColor={dropdownBorderColor}
        borderRadius={dropdownBorderRadius}
        borderSize={dropdownBorderSize}
        borderStyle={dropdownBorderStyle}
        filterColor={defaultColor}
        onClick={() => setIsSelectorOpen(prev => !prev)}
      >
        <span id="placeholder_selection">
          {isRangeSelected ? (
            <>
              {minValue} - {maxValue}{" "}
              <i className="fas fa-times" onClick={() => onResetSelection()} />
            </>
          ) : (
            placeholder
          )}
        </span>

        <i id="selector" className={`fas fa-caret-${isSelectorOpen ? "down" : "right"}`} />
      </FilterContainer>
      {isSelectorOpen && (
        <SelectorContainer ref={selectorRef}>
          <InputContainer>
            <input
              max={maxRange}
              min={minRange}
              name="minValueInput"
              step={inputStep}
              type="number"
              value={minValue}
              onBlur={() => onSwapValuesIfNecessary()}
              onChange={e => onChangeValue(e)}
            />
            -
            <input
              max={maxRange}
              min={minRange}
              name="maxValueInput"
              step={inputStep}
              type="number"
              value={maxValue}
              onBlur={() => onSwapValuesIfNecessary()}
              onChange={e => onChangeValue(e)}
            />
          </InputContainer>

          <SliderContainer
            sliderColor={sliderColor}
            backgroundSliderProperty={backgroundSliderProperty}
          >
            <div id="slider"></div>
            <input
              max={maxRange}
              min={minRange}
              name="minValueSlider"
              step={inputStep}
              type="range"
              value={minValue}
              onChange={e => onSlide(e)}
            />
            <input
              max={maxRange}
              min={minRange}
              name="maxValueSlider"
              step={inputStep}
              type="range"
              value={maxValue}
              onChange={e => onSlide(e)}
            />
          </SliderContainer>
        </SelectorContainer>
      )}
    </Container>
  )
}

RangeFilter.defaultProps = {
  // Props
  placeholder: "Example of Range Filter",
  data: {
    unique: [],
  },
  // Style
  activeBorder: false,
  activeRadius: false,
  defaultColor: "#01014B",
  dropdownBorderColor: "#F6F6F6",
  dropdownBorderRadius: "5px",
  dropdownBorderSize: "3px",
  dropdownBorderStyle: "",
  sliderColor: "#01014B",
}

RangeFilter.propTypes = {
  // Props
  placeholder: PropTypes.string,
  data: PropTypes.shape({
    unique: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  }),
  // Style
  activeBorder: PropTypes.bool,
  activeRadius: PropTypes.bool,
  defaultColor: PropTypes.string,
  dropdownBorderColor: PropTypes.string,
  dropdownBorderRadius: PropTypes.string,
  dropdownBorderSize: PropTypes.string,
  dropdownBorderStyle: PropTypes.string,
  sliderColor: PropTypes.string,
}

export default RangeFilter
